/**
 * useFragility (P162) -- the fragility radar's ranked concentration-risk
 * watchlist. Live/current structural scan; no scenario input.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type { FragilityReport } from "shared/api/types";

export function useFragility(limit = 20): UseQueryResult<FragilityReport, ApiError> {
  return useQuery({
    queryKey: ["twin", "fragility", limit],
    queryFn: () => api.get<FragilityReport>(`/v1/twin/fragility?limit=${limit}`),
  });
}

export interface FragilityChangePart {
  input: string;
  from: number | string | null;
  to: number | string | null;
  points: number;
}

export interface FragilityMover {
  entity_kind: string;
  entity_id: string;
  name: string;
  since: string;
  from_score: number;
  to_score: number;
  delta: number;
  parts: FragilityChangePart[];
  unexplained: number;
  unexplained_reason: "weighting" | "unstored_input" | null;
}

export interface FragilityChanges {
  days: number;
  readings: number;
  moved: number;
  movers: FragilityMover[];
  truncated: boolean;
}

/** Why fragility scores moved over the last `days`. */
export function useFragilityChanges(
  days = 30,
): UseQueryResult<FragilityChanges, ApiError> {
  return useQuery({
    queryKey: ["twin", "fragility-changes", days],
    queryFn: () => api.get<FragilityChanges>(`/v1/twin/fragility/changes?days=${days}`),
  });
}
